import produce from 'immer';
import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  InferAnnotationParamsResponse,
  inferAnnotationParamsWithAi,
} from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AnnotationAssistantDAO';
import {PlateMap, PlateMapWell} from 'samples/plate-maps/PlateMapConstants';
import {formatGridCoordinates} from 'samples/SampleUtil';
import {maybeLogErrorResponseToSentry} from 'util/RequestCorrelation';

type WellWithRole = PlateMapWell & {role: ExperimentalRole};

/** Suggested roles keyed by well coordinate (e.g. "A1"). */
interface SuggestedRolesByCoordinate {
  [coordinate: string]: ExperimentalRole;
}

export interface AIRoleAnnotationSuggestion {
  /** The current plate map with the AI-suggested roles applied, or null when no suggestion is pending. */
  aiSuggestedPlateMap: PlateMap | null;
  /** Drops the pending suggestion, if any, and ignores any response still in flight. */
  clearAISuggestion: () => void;
  /** Whether the grid preview is an AI suggestion (as opposed to a manual pattern preview). */
  isAIPreview: boolean;
  /** Submits a prompt and stores the resulting suggestion. Errors are returned, never thrown. */
  handleSubmitAIPrompt: (userPrompt: string) => Promise<InferAnnotationParamsResponse>;
  /** Commits the pending suggestion through `onApply` and clears it. */
  applyAISuggestion: () => void;
  /** Drops the pending suggestion without applying it. */
  discardAISuggestion: () => void;
}

function getRolesByCoordinate(plateMap: PlateMap): SuggestedRolesByCoordinate {
  return plateMap.wells
    .filter((well): well is WellWithRole => well.role != null)
    .reduce((acc: SuggestedRolesByCoordinate, well) => {
      acc[formatGridCoordinates(well.position.row, well.position.column)] = well.role;
      return acc;
    }, {});
}

/**
 * Returns the plate map with every well's role replaced by the suggestion. Wells the suggestion does not
 * mention are cleared, so the result is a complete role layout; all other layers are left untouched.
 */
function applySuggestedRoles(plateMap: PlateMap, suggestedRoles: SuggestedRolesByCoordinate): PlateMap {
  return produce(plateMap, (draft) => {
    draft.wells.forEach((well) => {
      const coordinate = formatGridCoordinates(well.position.row, well.position.column);
      well.role = suggestedRoles[coordinate] ?? null;
    });
  });
}

/**
 * State and handlers for the Plate Annotator AI tab in WellRoleAssignmentSettings.
 *
 * Owns the pending suggestion and keeps the caller's preview plate map in sync with it, so any plate map
 * editor that renders WellRoleAssignmentSettings can offer AI role suggestions with the same behavior:
 * the suggestion is previewed on the grid until the user applies or discards it.
 *
 * Only the suggested roles are stored. The previewed and applied plate maps are always derived from the
 * caller's current plate map, so edits to other layers made while a response is in flight, or while a
 * suggestion is being previewed, are preserved when the suggestion is applied.
 */
export default function useAIRoleAnnotationSuggestion({
  plateMap,
  setPlateMapWithPreview,
  onApply,
  onDiscard,
}: {
  plateMap: PlateMap;
  /**
   * Setter for the grid preview shared by every settings panel. Receives the suggested plate map while a
   * suggestion is pending. When the suggestion is cleared, the preview is reset only if it still shows
   * that suggestion, so a panel that has since taken over the preview is left alone.
   */
  setPlateMapWithPreview: Dispatch<SetStateAction<PlateMap | null>>;
  /** Called with the suggested plate map when the user applies it. */
  onApply: (suggestedPlateMap: PlateMap) => void;
  /** Called when the user discards a pending suggestion. */
  onDiscard?: () => void;
}): AIRoleAnnotationSuggestion {
  const [suggestedRoles, setSuggestedRoles] = useState<SuggestedRolesByCoordinate | null>(null);
  // Incremented whenever the suggestion is cleared. A request only stores its response if no clear
  // happened while it was in flight, so leaving the Plate Annotator tab (or the role layer) before the
  // annotator answers does not resurrect the suggestion with no way to act on it.
  const requestGenerationRef = useRef(0);

  const clearAISuggestion = useCallback((): void => {
    requestGenerationRef.current += 1;
    setSuggestedRoles(null);
  }, []);

  const aiSuggestedPlateMap = useMemo(
    () => (suggestedRoles != null ? applySuggestedRoles(plateMap, suggestedRoles) : null),
    [plateMap, suggestedRoles]
  );
  // A suggestion can only exist while the Plate Annotator tab is showing, so its presence is exactly
  // "the grid preview is the AI suggestion".
  const isAIPreview = aiSuggestedPlateMap != null;

  // The suggested plate map most recently pushed into the shared preview, so clearing can tell whether
  // the preview is still ours.
  const pushedPreviewRef = useRef<PlateMap | null>(null);
  useEffect(() => {
    if (aiSuggestedPlateMap != null) {
      pushedPreviewRef.current = aiSuggestedPlateMap;
      setPlateMapWithPreview(aiSuggestedPlateMap);
    } else {
      const pushedPreview = pushedPreviewRef.current;
      pushedPreviewRef.current = null;
      if (pushedPreview != null) {
        setPlateMapWithPreview((current) => (current === pushedPreview ? null : current));
      }
    }
  }, [aiSuggestedPlateMap, setPlateMapWithPreview]);

  const handleSubmitAIPrompt = async (userPrompt: string): Promise<InferAnnotationParamsResponse> => {
    const requestGeneration = requestGenerationRef.current;
    let res: InferAnnotationParamsResponse;
    try {
      res = await inferAnnotationParamsWithAi({
        currentRoles: getRolesByCoordinate(plateMap),
        dimensions: {rows: plateMap.size.numRows, columns: plateMap.size.numColumns},
        userPrompt,
      });
      if (res.error == null && requestGeneration === requestGenerationRef.current) {
        setSuggestedRoles(res.roleAnnotations ?? {});
      }
      // this error is not from the llm, but from the task API endpoint
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      maybeLogErrorResponseToSentry(error);
      // this will be shown to the user
      res = {
        error: 'An unexpected error occurred submitting your prompt',
        roleAnnotations: null,
        traceId: '',
      };
    }
    return res;
  };

  function applyAISuggestion(): void {
    if (aiSuggestedPlateMap != null) {
      onApply(aiSuggestedPlateMap);
    }
    clearAISuggestion();
  }

  function discardAISuggestion(): void {
    onDiscard?.();
    clearAISuggestion();
  }

  return {
    aiSuggestedPlateMap,
    clearAISuggestion,
    isAIPreview,
    handleSubmitAIPrompt,
    applyAISuggestion,
    discardAISuggestion,
  };
}
